/// Import libraries
import { err, ok, Result } from "neverthrow";
import axios from "axios";
/// Import project files
import ErrorLogServiceError from "@/framework/logger/errorLog/errorLogServiceError";
import store from "@/store";
import { i18n } from "@/framework/plugins/i18n";

export type ErrorLog = {
  /** Authentication type of the occurred error */
  authType?: string;
  /** Name of the function that called this method. */
  errorFunction: string;
  /** Parameters of the function that called this method. */
  variables: any;
  /** Errors track trace */
  stackTrace?: string;
};

export interface IErrorLogService {
  /**
   * Convenience method to send a `GraphQL` error log to the Kotozna Error log API.
   * @param paramQuery GraphQL param query
   * @param variables GraphQL variables
   * @param errorMessage Error message
   * @returns Result string or ErrorLogServiceError
   */
  sendLog(paramQuery: string, variables: string, errorMessage: string): Promise<Result<string, ErrorLogServiceError>>;

  /**
   * Convenience method to send an `Auth` error log to the Kotozna Error log API.
   * @param authType Auth type of the occurred error
   * @param errorFunction Function name where the error happened
   * @param variables Input variables
   * @param stackTrace Stacktrace as string
   * @returns Result string or ErrorLogServiceError
   */
  sendAuthLog(
    authType: string,
    errorFunction: string,
    variables: any,
    stackTrace: string
  ): Promise<Result<string, ErrorLogServiceError>>;

  /**
   * Sends an error log to the Kotozna Error log API
   * @param errorCode Kotozna error code
   * @param systemData System information
   * @param customText Custom text
   * @param stacktrace Stacktrace as string
   * @returns Result string or ErrorLogServiceError
   */
  send(
    errorCode: string,
    systemData: string,
    customText: string,
    stacktrace: string
  ): Promise<Result<string, ErrorLogServiceError>>;
}

/**
 * Kotozna Error Log Service
 */
export class ErrorLogService implements IErrorLogService {
  private readonly _logURL: string;

  constructor(logURL: string = `/accesslog/error-log`) {
    this._logURL = logURL;
  }

  /**
   * Convenience method to send a `GraphQL` error log to the Kotozna Error log API.
   * @param paramQuery GraphQL param query
   * @param variables GraphQL variables
   * @param errorMessage Error message
   * @param stackTrace
   * @returns Result string or ErrorLogServiceError
   */
  sendLog = async (
    paramQuery: string,
    variables: string,
    errorMessage: string,
    stackTrace: string = ""
  ): Promise<Result<string, ErrorLogServiceError>> => {
    const systemData = `paramQuery:${paramQuery}\nvariables:${variables}\nstackTrace:${stackTrace}`;
    return await this.send("ERR_FATAL", systemData, "graphql", errorMessage);
  };

  /**
   * Convenience method to send an `Auth` error log to the Kotozna Error log API.
   * @param authType Auth type of the occurred error
   * @param errorFunction Function name where the error happened
   * @param variables Input variables
   * @param stackTrace Stacktrace as string
   * @returns Result string or ErrorLogServiceError
   */
  sendAuthLog = async (
    authType: string,
    errorFunction: string,
    variables: any,
    stackTrace: string
  ): Promise<Result<string, ErrorLogServiceError>> => {
    const systemData = `errorFunction:${errorFunction} \nvariables:${variables}`;
    return await this.send("ERR_FATAL", systemData, authType, stackTrace);
  };

  /**
   * Sends an error log to the Kotozna Error log API
   * @param errorCode Kotozna error code
   * @param systemData System information
   * @param customText Custom text
   * @param stacktrace Stacktrace as string
   * @param createdAt (Optional) Timestamp
   * @returns Result string or ErrorLogServiceError
   */
  send = async (
    errorCode: string,
    systemData: string,
    customText: string,
    stacktrace: string,
    createdAt: number = new Date().getTime() / 1000
  ): Promise<Result<string, ErrorLogServiceError>> => {
    let accessId = "";
    if (store && store.getters.user && store.getters.user.id) {
      accessId = store.getters.user.id;
    } else if (store && store.getters.staff && store.getters.staff.myself && store.getters.staff.myself.access_id) {
      accessId = store.getters.staff.myself.access_id;
    }

    const request = {
      error_code: errorCode,
      system_name: "FE-PWA",
      system_path: "",
      system_data: systemData,
      system_identifier: accessId,
      lang_cd: i18n.locale,
      custom_text: customText,
      stacktrace_text: stacktrace,
      created_at: createdAt,
    };

    try {
      const response = await axios.post(this._logURL, request);
      // data: { message: "request accepted." }
      return ok(response.data.message);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // Access to config, request, and response (5xx, 4xx)
        return err(new ErrorLogServiceError(ErrorLogServiceError.Type.networkError, error));
      } else {
        return err(new ErrorLogServiceError(ErrorLogServiceError.Type.responseError, error));
      }
    }
  };
}

export default new ErrorLogService();
